

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchVisitLocations } from "../redux/actions/VisitLocationActions";
import { 
  FiMapPin, 
  FiRefreshCw, 
  FiArrowLeft, 
  FiInfo, 
  FiNavigation,
  FiPlus,
  FiHash
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";

const VisitLocation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { locations, loading, error } = useSelector((state) => state.VisitLocation);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    dispatch(fetchVisitLocations());
  }, [dispatch]);

  // Pagination calculations
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentLocations = locations.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(locations.length / itemsPerPage);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400 text-lg">Loading Locations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/dashboard")}
              className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <FiArrowLeft className="text-lg" />
              Back to Dashboard
            </button>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate("/dashboard/Create-Visit-Location")}
              className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-xl transition-colors"
            >
              <FiPlus className="text-lg" />
              Create Location
            </button>
            <button
              onClick={() => dispatch(fetchVisitLocations())}
              disabled={loading}
              className="flex items-center gap-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 py-2 px-4 rounded-xl border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all"
            >
              <FiRefreshCw className={`text-lg ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Header Section */}
          <div className="bg-gradient-to-r from-blue-500 to-cyan-500 px-6 py-8">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <FiMapPin className="text-2xl text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white mb-2">
                  Visit Locations
                </h1>
                <p className="text-blue-100">
                  Manage all available visit locations for tasks and assignments
                </p>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="p-6">
            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                    <FiInfo className="text-white text-sm" />
                  </div>
                  <div>
                    <p className="text-red-800 dark:text-red-300 font-medium">
                      Error loading locations
                    </p>
                    <p className="text-red-600 dark:text-red-400 text-sm">
                      {error}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                    <FiMapPin className="text-white text-lg" />
                  </div>
                  <div>
                    <p className="text-blue-600 dark:text-blue-400 text-sm font-medium">Total Locations</p>
                    <p className="text-blue-900 dark:text-blue-100 text-2xl font-bold">{locations.length}</p>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                    <FiNavigation className="text-white text-lg" />
                  </div>
                  <div>
                    <p className="text-green-600 dark:text-green-400 text-sm font-medium">Active Locations</p>
                    <p className="text-green-900 dark:text-green-100 text-2xl font-bold">{locations.length}</p>
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                    <FiHash className="text-white text-lg" />
                  </div>
                  <div>
                    <p className="text-purple-600 dark:text-purple-400 text-sm font-medium">Available</p>
                    <p className="text-purple-900 dark:text-purple-100 text-2xl font-bold">{locations.length}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl border border-gray-200 dark:border-gray-600 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-100 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                      <th className="py-4 px-6 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <FiHash className="text-sm" />
                          #
                        </div>
                      </th>
                      <th className="py-4 px-6 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <FiMapPin className="text-sm" />
                          Location Name
                        </div>
                      </th>
                      <th className="py-4 px-6 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <FiNavigation className="text-sm" />
                          Status
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                    {currentLocations.length > 0 ? (
                      currentLocations.map((location, index) => (
                        <tr 
                          key={location.id || index} 
                          className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                        >
                          <td className="py-4 px-6">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {indexOfFirstItem + index + 1}
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                                <FiMapPin className="text-blue-600 dark:text-blue-400 text-sm" />
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {location.location_name}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  ID: {location.id || `LOC-${index + 1}`}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                              <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                              Active
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="py-8 px-6 text-center">
                          <div className="flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
                            <FiMapPin className="text-3xl mb-2 opacity-50" />
                            <p className="text-lg font-medium">No locations found</p>
                            <p className="text-sm">There are no visit locations available at the moment.</p>
                            <button
                              onClick={() => navigate("/dashboard/create-location")}
                              className="mt-4 flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-xl transition-colors"
                            >
                              <FiPlus className="text-sm" />
                              Add First Location
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-white dark:bg-gray-800 px-6 py-4 border-t border-gray-200 dark:border-gray-600">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{" "}
                      <span className="font-medium">
                        {Math.min(indexOfLastItem, locations.length)}
                      </span>{" "}
                      of <span className="font-medium">{locations.length}</span> locations
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Information Card */}
        <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-3">
            💡 Visit Locations Management
          </h3>
          <ul className="text-blue-800 dark:text-blue-200 text-sm space-y-2">
            <li>• Visit locations are used when assigning tasks to employees</li>
            <li>• Each location represents a physical place where work needs to be done</li>
            <li>• Locations help in organizing and tracking field operations</li>
            <li>• Use the "Add Location" button to create new visit locations</li>
            <li>• All locations are available for task assignments immediately after creation</li>
            <li>• Refresh the list to see the most current locations</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default VisitLocation;